import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, TouchableOpacity, View } from "react-native";
import sitesHelper from "../../../../API/Sites/sitesHelper";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import AgentBuilderSidebar from "../../../Agents/Components/AgentBuilderSidebar";
import SitesSettings from "./SitesSettings";

const HASH_ROUTED_SECTIONS = ["profile", "behaviour", "verification"];

const buildInitialValues = () => ({
  name: "",
  description: "",
  status: "",
  validationToken: "",
  url: "",
  customSitemap: "",
  freeRoaming: false,
  useProxy: false,
  extractImages: false,
  imgRetrievalPrompt: "",
  scrapingCycle: 0,
  topics: [],
});

const fromSiteRecord = (site, topics) => {
  let parsedTopics = site?.topics;
  if (typeof parsedTopics === "string") {
    try {
      parsedTopics = JSON.parse(parsedTopics);
    } catch {
      parsedTopics = [];
    }
  }
  const topicOptions = topics || [];
  return {
    ...site,
    topics: (parsedTopics || []).map((id) => {
      const topic = topicOptions.find((u) => u?.id === id);
      return {
        ...(topic || { id }),
        value: id,
      };
    }),
  };
};

const SiteBuilderScreenContainer = () => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const params = useParams();
  const workspaceId = params?.workspaceId;
  const siteId = params?.siteId;
  const isEditMode = !!siteId && siteId !== "new";
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const AppToasty = AppToast.useToast();
  const formRef = useRef();
  const scrollRef = useRef();
  const [showUnsavedChoice, setShowUnsavedChoice] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const unblockRef = useRef(null);
  const { getActiveWorkspace, getWorkspaceEntities } = useWorkspaces();

  const [activeSection, setActiveSection] = useState("profile");
  const [builderCtx, setBuilderCtx] = useState(null);
  const builderCtxRef = useRef(null);
  const [saveVersion, setSaveVersion] = useState(0);
  const [initialMapValues, setInitialMapValues] = useState(null);
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const createInitRef = useRef(false);
  const editInitRef = useRef(null);

  const _topics = useMemo(() => {
    return (getWorkspaceEntities("topic") || []).map((topic) => ({
      ...topic,
      name: topic["label"],
      category: topic["type"],
      value: topic["id"],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.entities, state?.activeWorkspaceID]);

  const selectedSite = useMemo(
    () => (state?.sites || []).find((u) => u?.id === siteId),
    [state?.sites, siteId]
  );

  const loadSites = useCallback(
    async (workspaceID) => {
      try {
        setIsSitesLoading(true);
        let resultSites = await sitesHelper?.getSitesByWorkspaceID(workspaceID);
        resultSites = resultSites?.data?.sitesByWorkspace?.items?.filter(
          (u) => u?._deleted != true
        );
        dispatch("SET_SITES", resultSites || []);
      } catch (error) {
        console.error("Error loading sites:", error);
        dispatch("SET_SITES", []);
      } finally {
        setIsSitesLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    const sites = state?.sites || [];
    const belongsToWorkspace = sites.some((u) => u?.workspaceID === wsId);
    if (sites.length === 0 || !belongsToWorkspace) {
      loadSites(wsId);
    } else {
      setIsSitesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getActiveWorkspace()?.id]);

  useEffect(() => {
    if (!siteId) {
      history.replace(`/workspaces/${workspaceId}/settings/sites`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, workspaceId]);

  useEffect(() => {
    if (isEditMode) return;
    if (siteId !== "new") return;
    if (createInitRef.current) return;
    createInitRef.current = true;
    setInitialMapValues(buildInitialValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;
    if (isSitesLoading) return;
    if (editInitRef.current === siteId) return;
    const activeWorkspaceId = getActiveWorkspace()?.id;
    if (!activeWorkspaceId) return;
    const site = (state?.sites || []).find((u) => u?.id === siteId);
    if (!site) {
      const listIsStale = state?.sites?.some(
        (u) => u?.workspaceID && u?.workspaceID !== activeWorkspaceId
      );
      if (listIsStale) return;
      history.replace(`/workspaces/${activeWorkspaceId}/settings/sites`);
      return;
    }
    editInitRef.current = siteId;
    setInitialMapValues(fromSiteRecord(site, _topics));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    siteId,
    isEditMode,
    isSitesLoading,
    state?.sites,
    getActiveWorkspace()?.id,
    _topics,
  ]);

  const isSectionVisible = (sectionId) => {
    switch (sectionId) {
      case "verification":
        return isEditMode;
      default:
        return true;
    }
  };

  const sectionGroups = useMemo(() => {
    const groups = [
      {
        id: "setup",
        label: t("sites.builderGroupSetup", { defaultValue: "Set up" }),
        items: [
          {
            id: "profile",
            title: t("sites.builderSectionProfile", {
              defaultValue: "Overview",
            }),
            icon: AppAssets.icons.persona,
          },
          {
            id: "behaviour",
            title: t("sites.builderSectionBehaviour", {
              defaultValue: "Behaviour",
            }),
            icon: AppAssets.icons.launch,
          },
        ],
      },
      {
        id: "system",
        label: t("sites.builderGroupSystem", { defaultValue: "System" }),
        items: [
          {
            id: "verification",
            title: t("sites.builderSectionVerification", {
              defaultValue: "Verification",
            }),
            icon: AppAssets.icons.shield,
          },
        ],
      },
    ];
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => isSectionVisible(item.id)),
      }))
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, t]);

  useEffect(() => {
    if (!isSectionVisible(activeSection)) {
      setActiveSection("profile");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMapValues]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const applyHash = () => {
      const raw = (window.location.hash || "")
        .replace(/^#/, "")
        .trim()
        .toLowerCase();
      if (!raw) return;
      if (HASH_ROUTED_SECTIONS.includes(raw) && isSectionVisible(raw)) {
        setActiveSection(raw);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, isEditMode, initialMapValues]);

  useEffect(() => {
    builderCtxRef.current = builderCtx;
  }, [builderCtx]);

  useEffect(() => {
    unblockRef.current = history.block((location) => {
      const ctxIsDirty = builderCtxRef.current?.isDirty;
      const formikDirty = !!formRef?.current?.dirty;
      const isDirty = ctxIsDirty ?? formikDirty;
      if (!isDirty) return true;
      const builderPrefix = `/workspaces/${workspaceId}/sites/`;
      if (location?.pathname?.startsWith(builderPrefix)) return true;
      setPendingNavigation(location?.pathname);
      setShowUnsavedChoice(true);
      return false;
    });
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e) => {
      const isDirty =
        builderCtxRef.current?.isDirty ?? !!formRef?.current?.dirty;
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const handleStayOnBuilder = () => {
    setShowUnsavedChoice(false);
    setPendingNavigation(null);
  };

  const handleLeaveBuilder = () => {
    setShowUnsavedChoice(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    unblockRef.current?.();
    unblockRef.current = null;
    if (target) history.push(target);
  };

  const errorSections = builderCtx?.errorSections || [];
  const errorSectionsKey = JSON.stringify(errorSections);
  const prevErrorSectionsKeyRef = useRef("[]");
  useEffect(() => {
    if (prevErrorSectionsKeyRef.current === errorSectionsKey) return;
    prevErrorSectionsKeyRef.current = errorSectionsKey;
    if (errorSections.length === 0) return;
    const sectionTitles = sectionGroups
      .flatMap((g) => g.items)
      .filter((i) => errorSections.includes(i.id))
      .map((i) => i.title);
    if (sectionTitles.length === 0) return;
    setActiveSection(errorSections[0]);
    AppToasty.show(
      t("sites.builderValidationErrors", {
        defaultValue: "Some required fields need attention in: {{sections}}",
        sections: sectionTitles.join(", "),
      }),
      { type: "danger" }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorSectionsKey]);

  const lastHashTokenRef = useRef(null);
  useEffect(() => {
    const token = activeSection;
    if (!token || lastHashTokenRef.current === token) return;
    lastHashTokenRef.current = token;
    if (typeof window === "undefined") return;
    const currentHash = (window.location.hash || "")
      .replace(/^#/, "")
      .toLowerCase();
    if (currentHash === token) return;
    const { pathname, search } = window.location;
    window.history.replaceState(
      window.history.state || null,
      "",
      `${pathname}${search}#${token}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const isFormReady =
    initialMapValues != null &&
    !isSitesLoading &&
    (!isEditMode || initialMapValues?.id === siteId);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ y: 0, animated: false });
  }, [activeSection]);

  const handleSiteCreated = (status, savedId, submittedValues) => {
    if (status === "success") {
      AppToasty.show(t("siteCreationSuccessCaption"), { type: "success" });
      formRef?.current?.resetForm?.({
        values: submittedValues || formRef.current?.values,
      });
      setSaveVersion((v) => v + 1);
      if (!isEditMode && savedId) {
        createInitRef.current = false;
        history.replace(`/workspaces/${workspaceId}/sites/${savedId}`);
      }
    } else {
      AppToasty.show(t("siteCreationFailedCaption"), { type: "danger" });
    }
  };

  const renderSaveButton = () => (
    <ToothFairyButton
      title={t("common.save")}
      onPress={() => formRef?.current?.submitForm()}
      isDisabled={!isFormReady}
      wrapperStyle={{
        minWidth: 110,
        height: 36,
        marginTop: 10,
      }}
    />
  );

  const renderBody = () => {
    if (!isFormReady) {
      return (
        <View
          style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        >
          <ToothFairyLoadingPlaceholder
            caption={t("settings.list.loadingSites")}
          />
        </View>
      );
    }
    return (
      <View style={{ flex: 1, flexDirection: "row", minHeight: 0 }}>
        <AgentBuilderSidebar
          groups={sectionGroups}
          activeSection={activeSection}
          onSelect={setActiveSection}
          errorSections={errorSections}
        />
        <ScrollView
          ref={scrollRef}
          style={{ flex: 1, minWidth: 0 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator
        >
          <SitesSettings
            key={siteId}
            ref={formRef}
            initialMapValues={initialMapValues}
            isEdit={isEditMode}
            selectedId={isEditMode ? siteId : undefined}
            opt={isEditMode ? selectedSite : undefined}
            settingType="site"
            onFunctionCreated={handleSiteCreated}
            onSiteDeleted={() =>
              history.push(`/workspaces/${workspaceId}/settings/sites`)
            }
            requestValidation={() => {}}
            displayTokenSection={false}
            setDisplayTokenSection={() => {}}
            isBuilderLayout
            builderActiveSection={activeSection}
            onBuilderContextChange={setBuilderCtx}
            saveVersion={saveVersion}
          />
        </ScrollView>
      </View>
    );
  };

  return (
    <View
      style={{
        flex: 1,
        height: "100%",
        backgroundColor: currentTheme?.theme?.backgroundColor,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: currentTheme?.theme?.lightGrey,
        }}
      >
        <TouchableOpacity
          onPress={() =>
            history.push(`/workspaces/${workspaceId}/settings/sites`)
          }
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginRight: 14,
            paddingVertical: 4,
            paddingHorizontal: 6,
          }}
        >
          <AppIcon
            icon={AppAssets.icons.arrow_left}
            size={13}
            color={currentTheme?.theme?.darkGrey}
          />
          <AppText
            style={{ marginLeft: 6, fontSize: 13 }}
            color={currentTheme?.theme?.darkGrey}
          >
            {t("settings.sitesSection", { defaultValue: "Sites" })}
          </AppText>
        </TouchableOpacity>
        <AppText
          style={{ fontSize: 15, flexShrink: 1 }}
          fontWeight="600"
          numberOfLines={1}
        >
          {isEditMode
            ? initialMapValues?.url || selectedSite?.url || ""
            : t("sites.builderNewSite", { defaultValue: "New site mapping" })}
        </AppText>
        <View style={{ flex: 1 }} />
        {renderSaveButton()}
      </View>
      {renderBody()}
      <ToothFairyChoice
        cancelCaption={t("settings.qa.unsavedChangesKeepEditing", {
          defaultValue: "Keep editing",
        })}
        caption={t("settings.qa.unsavedChangesCaption", {
          defaultValue: "Unsaved changes will be lost",
        })}
        subCaption={t("sites.unsavedChangesSubCaption", {
          defaultValue:
            "This site mapping has changes that haven't been saved. Leave now and they're gone for good.",
        })}
        confirmCaption={t("settings.qa.leaveWithoutSaving", {
          defaultValue: "Discard changes",
        })}
        onCancelButtonPress={handleStayOnBuilder}
        onConfirmButtonPress={handleLeaveBuilder}
        setShowChoice={setShowUnsavedChoice}
        showChoice={showUnsavedChoice}
      />
    </View>
  );
};

export default SiteBuilderScreenContainer;
